import React from "react";
import { Lock } from "lucide-react";

import { Label } from "../../../components/ui/label";
import { cn } from "../../../utils/cn";

type Props = {
  label: string;
  /** Deliberately unknown — see toDisplayValue. */
  value?: unknown;
  /** Shown in place of the value when it's missing. */
  empty?: string;
};

/**
 * Several fields on this API come back as wrapper objects rather than strings
 * — email is { address, isVerified }, phone is { number, isVerified }. Passing
 * one straight to JSX throws "Objects are not valid as a React child" and takes
 * the whole settings page down, so unwrap the common shapes here and degrade to
 * blank rather than crashing on anything unexpected.
 */
const toDisplayValue = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object") {
    const wrapper = value as Record<string, unknown>;
    const inner = wrapper.address ?? wrapper.number ?? wrapper.name;
    if (typeof inner === "string") return inner;
  }
  return "";
};

/**
 * A verified field the merchant can see but not change. These are deliberately
 * displayed rather than hidden — merchants want to confirm what we hold on
 * record even when only support can amend it.
 */
const ReadOnlyField = ({ label, value, empty = "Not provided" }: Props) => {
  const display = toDisplayValue(value);

  return (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2 rounded-xl border bg-muted px-4 py-3">
        <span
          title={display || undefined}
          className={cn(
            "min-w-0 flex-1 truncate text-[14px] font-medium",
            display ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {display || empty}
        </span>
        <Lock className="size-3.5 shrink-0 text-muted-foreground" />
      </div>
    </div>
  );
};

export default ReadOnlyField;
